import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Sparkles } from "lucide-react";

const TIERS = [
  {
    name: "Essentials",
    description:
      "For smaller services getting started with integrated clinical governance.",
    highlight: false,
    features: [
      "Up to 3 core modules",
      "Standard risk engine",
      "Email support",
      "Cloud hosting & security",
      "Onboarding workshop",
    ],
  },
  {
    name: "Enterprise",
    description:
      "For hospitals and health services running full clinical governance programs.",
    highlight: true,
    features: [
      "All 7 modules + Embedded AI",
      "Advanced risk engine & KRIs",
      "API integrations (EMR, workforce)",
      "Priority support & CSM",
      "Custom onboarding & training",
    ],
  },
  {
    name: "Network",
    description:
      "For multi-site health networks and jurisdictional deployments.",
    highlight: false,
    features: [
      "Multi-entity risk consolidation",
      "Dedicated environment options",
      "Regulatory reporting packs",
      "24/7 enterprise support",
      "Bespoke implementation program",
    ],
  },
];

export default function Pricing() {
  return (
    <div data-testid="pricing-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white border-b border-slate-200">
        <div className="absolute inset-0 bg-dots opacity-70 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 py-20 md:py-28 text-center">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#22A34A] mb-4">
            Pricing
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
            Pricing that scales with your program.
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            Every hospital is different — team size, module scope, integration
            needs. We build a quote around your governance program, not the
            other way around.
          </p>
        </div>
      </section>

      {/* TIERS */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {TIERS.map((t, i) => (
              <div
                key={t.name}
                className={`relative rounded-3xl p-8 border transition-all ${
                  t.highlight
                    ? "bg-gradient-to-br from-[#0F172A] to-[#1F5D8C] text-white border-transparent shadow-xl md:-translate-y-2"
                    : "bg-white border-slate-100 shadow-sm hover:shadow-md"
                }`}
                data-testid={`pricing-tier-${t.name.toLowerCase()}`}
              >
                {t.highlight && (
                  <div className="absolute -top-3 left-8 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#22A34A] text-white text-[11px] font-bold tracking-[0.15em] uppercase">
                    <Sparkles className="w-3 h-3" />
                    Most Chosen
                  </div>
                )}
                <div
                  className={`text-xs font-bold tracking-[0.2em] uppercase mb-3 ${
                    t.highlight ? "text-[#3FB8E5]" : "text-[#22A34A]"
                  }`}
                >
                  {t.name}
                </div>
                <div
                  className={`font-heading text-3xl font-semibold tracking-tight mb-2 ${
                    t.highlight ? "text-white" : "text-slate-900"
                  }`}
                >
                  Request pricing
                </div>
                <p
                  className={`text-sm leading-relaxed mb-8 ${
                    t.highlight ? "text-white/70" : "text-slate-600"
                  }`}
                >
                  {t.description}
                </p>

                <ul className="space-y-3 mb-8">
                  {t.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <span
                        className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                          t.highlight ? "bg-white/15" : "bg-[#22A34A]/15"
                        }`}
                      >
                        <Check
                          className={`w-3 h-3 ${
                            t.highlight ? "text-[#3FB8E5]" : "text-[#22A34A]"
                          }`}
                        />
                      </span>
                      <span
                        className={`text-sm ${
                          t.highlight ? "text-white/85" : "text-slate-700"
                        }`}
                      >
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/book-demo"
                  data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                  className={`w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-medium transition-all hover:-translate-y-0.5 ${
                    t.highlight
                      ? "bg-[#22A34A] hover:bg-[#1e9042] text-white shadow-[0_10px_28px_-8px_rgba(34,163,74,0.55)]"
                      : "bg-slate-900 hover:bg-slate-800 text-white"
                  }`}
                >
                  Request a quote
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>

          <div className="mt-14 text-center text-sm text-slate-500 max-w-2xl mx-auto">
            All plans include cloud hosting, SOC-aligned security posture and
            regular platform updates. Pricing is based on organisation size,
            modules selected and integration scope.
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight">
            Not sure what you need? Let&apos;s talk it through.
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            A 60-minute call is usually enough to shape the right package for
            your program.
          </p>
          <Link
            to="/book-demo"
            data-testid="pricing-footer-book-demo-btn"
            className="mt-8 inline-flex items-center gap-2 bg-[#22A34A] hover:bg-[#1e9042] text-white rounded-full px-8 py-3.5 font-medium transition-all hover:-translate-y-0.5"
          >
            Book a Demo
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
